import { Text, View } from "@react-pdf/renderer";
import { useStyles } from "@pages/platform-analysis/components/buttons/export-pdf/styles";
import { useTheme } from "@mui/material";
import { PDFTableBody } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-body";
import { SelectedProductDTO } from "@models/platform-analysis/entities/steps/feature";
import { PDFTable } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables";
import { PDFTableRow } from "@pages/platform-analysis/components/buttons/export-pdf/components/layouts/tables/table-row";
import { displayCurrencyNumber } from "@pages/platform-analysis/util";
import { hexToRGBA, thousandSeparator } from "@utils";
import { PDFPortfolioCompositionTableConfigs as Configs } from "@pages/platform-analysis/components/buttons/export-pdf/components/sections/portfolio-composition/configs";
import { PDFTitleSection } from "@pages/platform-analysis/components/buttons/export-pdf/components/title-section";
import { PortfolioCompositionRowDTO } from "@models/platform-analysis/entities/steps/summary";
import { PDFSummaryAnalysisHeaderV2 } from "@pages/platform-analysis/components/buttons/export-pdf/components/tables/components/summary-analysis-header-v2";
import { isNil } from "lodash";
import { MaxAccountBalance } from "@pages/platform-analysis/_id/steps/fee/calculator/config";

type PDFFeePortfolioCompositionProps = {
  selectedProducts: SelectedProductDTO[];
  rows?: PortfolioCompositionRowDTO[];
  numOfProducts?: number;
  startSliceProductIndex?: number;
  endSliceProductIndex?: number;

  belowMinimumInvestmentSize?: boolean;
  isAppending?: boolean;
  isFollowingTable?: boolean;
  shouldHideSubTotal?: boolean;
  numOfHiddenProducts?: number;
};

export const PDFFeePortfolioCompositionSection = ({
  selectedProducts,
  rows = [],
  numOfProducts = 0,
  startSliceProductIndex = 0,
  endSliceProductIndex = 0,
  belowMinimumInvestmentSize,
  isAppending,
  isFollowingTable,
  shouldHideSubTotal,
  numOfHiddenProducts,
}: PDFFeePortfolioCompositionProps) => {
  const { typographyStyle } = useStyles();
  const { palette } = useTheme();

  const renderTableTitle = () => {
    if (selectedProducts.length === 0) return null;
    return (
      <View style={{ height: Configs.TableTitleHeight, marginTop: -1 }}>
        <Text
          style={{
            ...typographyStyle.body1,
            color: palette.common.black,
          }}
        >
          {selectedProducts.length === 1
            ? `(Product ${startSliceProductIndex + 1} of ${numOfProducts})`
            : `(Products ${
                startSliceProductIndex + 1
              }-${endSliceProductIndex} of ${numOfProducts})`}
        </Text>
      </View>
    );
  };

  const renderTitle = () => {
    if (!isAppending) {
      return (
        <>
          <PDFTitleSection
            title={"Fee analysis (continued)"}
            subTitle={"Portfolio composition by platform"}
          />
          {renderTableTitle()}
        </>
      );
    }

    return isAppending || isFollowingTable ? (
      <>
        <View style={{ height: Configs.TableTitleHeight }}>
          <Text
            style={{
              ...typographyStyle.subtitle,
              color: palette.common.black,
            }}
          >
            {"Portfolio composition by platform"}
          </Text>
        </View>
        {renderTableTitle()}
      </>
    ) : (
      <>
        <PDFTitleSection
          title={"Fee analysis (continued)"}
          subTitle={"Portfolio composition by platform"}
        />
        {renderTableTitle()}
      </>
    );
  };

  const renderNumberValue = (value: number) => {
    if (value === 0) return value;

    if (value >= MaxAccountBalance) {
      const roundedValue = Math.ceil(value);
      return displayCurrencyNumber(roundedValue);
    }

    const fixedValue = Number(value.toFixed(Configs.FixedDecimalPlaces));
    const showDecimalPlaces = fixedValue % 1 !== 0;
    return thousandSeparator(
      value.toFixed(showDecimalPlaces ? Configs.FixedDecimalPlaces : 0),
    );
  };

  return (
    <View style={{ position: "relative" }}>
      {renderTitle()}

      {selectedProducts.length === 0 && (
        <View style={{ gap: "5px", marginTop: 15, marginBottom: 15 }}>
          <Text
            style={{
              ...typographyStyle.body1,
              fontWeight: "bold",
              color: "#b1b6bf",
            }}
          >
            No data
          </Text>
          <Text style={{ ...typographyStyle.body1, color: "#b1b6bf" }}>
            No products match the criteria in your filter
          </Text>
        </View>
      )}

      {selectedProducts.length > 0 && (
        <>
          <PDFTable
            tableWidth={
              Configs.FirstCellWidth +
              selectedProducts.length * Configs.FollowingCellWidth
            }
          >
            {/* Header */}
            <PDFSummaryAnalysisHeaderV2
              type={"HeaderDoubleLineNoLogo"}
              showSubProductName
              forceShowBorderBottom
              selectedProducts={selectedProducts}
              title={"Portfolio composition by platform"}
              staticFirstCellWidth={Configs.FirstCellWidth}
              staticFollowingCellWidth={Configs.FollowingCellWidth}
              titleFontSize={Configs.RowNameFontSize}
              imageSize={0}
            />

            {/* Body */}
            <PDFTableBody>
              {rows?.map((row, index) => {
                const {
                  name: rowName,
                  cells,
                  isSubTotal,
                  isSingleRow,
                } = row || {};
                return (
                  <PDFTableRow
                    key={`PDFTableRow-${index}`}
                    rowKey={`PDFTableRow-${index}`}
                    color={isSubTotal || isSingleRow ? "gray" : "white"}
                  >
                    <View
                      style={{
                        width: Configs.FirstCellWidth,
                        alignItems: "flex-start",
                        paddingLeft: 5,
                        paddingRight: 5,
                        paddingTop: 4,
                        paddingBottom: 4,
                        flexDirection: "column",
                        borderColor: "#7C8493",
                        borderRightWidth: 1,
                      }}
                    >
                      <View
                        style={{
                          paddingLeft:
                            !isSubTotal && !shouldHideSubTotal ? 5 : 0,
                        }}
                      >
                        <Text
                          style={{
                            ...typographyStyle.body2,
                            color: palette.text.primary,
                            fontSize: Configs.RowNameFontSize,
                          }}
                        >
                          {rowName}
                        </Text>
                      </View>
                    </View>

                    {cells.map((cell, cellIndex) => {
                      const {
                        value,
                        valueInPercent,
                        belowMinimumInvestmentSize,
                      } = cell || {};
                      const numberValue = Number(value);
                      const numberValueInPercent = Number(valueInPercent);
                      const invalidValues =
                        isNaN(numberValue) || isNaN(numberValueInPercent);

                      if (!value || !valueInPercent) return <></>;

                      return (
                        <View
                          key={`PDFTableCell-${index}-${cellIndex}`}
                          style={{
                            width: Configs.FollowingCellWidth,
                            borderRight: "1px solid #7C8493",
                            position: "relative",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <View
                            style={{
                              width: "1px",
                              position: "absolute",
                              height: "100%",
                              top: 0,
                              left: "60%",
                              backgroundColor: hexToRGBA("#7C8493", 0.5),
                            }}
                          />

                          <View
                            style={{
                              width: "100%",
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              justifyContent: "center",
                              paddingTop: 4,
                              paddingBottom: 4,
                            }}
                          >
                            {invalidValues ? (
                              <View
                                style={{
                                  width: "60%",
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                  paddingRight: "3%",
                                }}
                              >
                                <Text
                                  style={{
                                    ...typographyStyle.body2,
                                    fontSize: Configs.DefaultFontSize,
                                  }}
                                >
                                  -
                                </Text>
                              </View>
                            ) : (
                              <View
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  width: "60%",
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                  paddingRight: "3%",
                                }}
                              >
                                {belowMinimumInvestmentSize && (
                                  <Text
                                    style={{
                                      ...typographyStyle.body2,
                                      color: palette.error.main,
                                      fontSize: Configs.DefaultFontSize,
                                    }}
                                  >
                                    *
                                  </Text>
                                )}
                                <Text
                                  style={{
                                    ...typographyStyle.body2,
                                    color: palette.text.primary,
                                    fontSize: Configs.DefaultFontSize,
                                  }}
                                >
                                  ${renderNumberValue(numberValue)}
                                </Text>
                              </View>
                            )}

                            {invalidValues ? (
                              <View
                                style={{
                                  width: "40%",
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                  paddingRight: "3%",
                                }}
                              >
                                <Text
                                  style={{
                                    ...typographyStyle.body2,
                                    fontSize: Configs.DefaultFontSize,
                                  }}
                                >
                                  -
                                </Text>
                              </View>
                            ) : (
                              <View
                                style={{
                                  width: "40%",
                                  alignItems: "flex-end",
                                  justifyContent: "flex-end",
                                  display: "flex",
                                  flexDirection: "row",
                                  paddingRight: "3%",
                                }}
                              >
                                <Text
                                  style={{
                                    ...typographyStyle.body2,
                                    color: palette.text.primary,
                                    fontSize: Configs.DefaultFontSize,
                                  }}
                                >
                                  {renderNumberValue(numberValueInPercent)}%
                                </Text>
                              </View>
                            )}
                          </View>
                        </View>
                      );
                    })}
                  </PDFTableRow>
                );
              })}
            </PDFTableBody>
          </PDFTable>
          {belowMinimumInvestmentSize && (
            <View style={{ marginTop: 5 }}>
              <Text
                style={{
                  ...typographyStyle.body2,
                  color: palette.error.main,
                }}
              >
                * Below minimum investment size
              </Text>
            </View>
          )}
        </>
      )}

      {!isNil(numOfHiddenProducts) && (
        <View style={{ marginTop: 15 }}>
          <Text
            style={{
              ...typographyStyle.body2,
              color: "#1E1970",
              fontWeight: "bold",
            }}
          >
            {`${numOfHiddenProducts} ${
              numOfHiddenProducts === 1 ? "product" : "products"
            } hidden`}
          </Text>
        </View>
      )}
    </View>
  );
};
